import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Description,
  Field,
  FieldGroup,
  Label,
} from "../../design/fieldset";
import { Input } from "../../design/input";
import { Select } from "../../design/select";
import { Subheading } from "../../design/heading";
import { useBusiness } from "../../contexts/BusinessContext";
import { useAccount } from "../../contexts/AccountContext";
import { Text } from "../../design/text";
import { CheckIcon } from "@heroicons/react/20/solid";
import {
  OrganizationType,
  NonprofitStatus,
  YearsInOperationRange,
  MonthlyCasesRange,
  ServiceFocus,
  OrganizationProfile,
} from "@visitproof/types";

export interface OrganizationProfileFormRef {
  submitForm: () => Promise<void>;
}

interface Props {
  isSaving?: boolean;
  onSuccessfulSubmit?: () => void;
}

// Label mappings for display
const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  for_profit: "For-Profit Business",
  nonprofit: "Nonprofit Organization",
  government: "Government Agency",
  educational: "Educational Institution",
  religious: "Religious Organization",
  individual: "Individual/Sole Proprietor",
  other: "Other",
};

const NONPROFIT_STATUS_LABELS: Record<NonprofitStatus, string> = {
  "501c3": "501(c)(3) - Charitable",
  "501c4": "501(c)(4) - Social Welfare",
  other: "Other Nonprofit Status",
  pending: "Pending Determination",
};

const YEARS_IN_OPERATION_LABELS: Record<YearsInOperationRange, string> = {
  less_than_1: "Less than 1 year",
  "1_to_3": "1-3 years",
  "3_to_5": "3-5 years",
  "5_to_10": "5-10 years",
  "10_plus": "10+ years",
};

const MONTHLY_CASES_LABELS: Record<MonthlyCasesRange, string> = {
  "1_to_10": "1-10 cases",
  "11_to_25": "11-25 cases",
  "26_to_50": "26-50 cases",
  "51_to_100": "51-100 cases",
  "100_plus": "100+ cases",
};

const SERVICE_FOCUS_OPTIONS: { value: ServiceFocus; label: string }[] = [
  { value: "supervised_visitation", label: "Supervised Visitation" },
  { value: "therapeutic_visitation", label: "Therapeutic Visitation" },
  { value: "exchanges_only", label: "Exchanges Only" },
  { value: "reunification_therapy", label: "Reunification Therapy" },
  { value: "monitoring_only", label: "Monitoring Only" },
  { value: "parent_education", label: "Parent Education" },
  { value: "other", label: "Other Services" },
];

const OrganizationProfileForm = forwardRef<OrganizationProfileFormRef, Props>(
  ({ onSuccessfulSubmit }, ref) => {
    const { account } = useAccount();
    const { business, updateBusiness } = useBusiness();

    // Initialize form state from existing business data
    const [data, setData] = useState<OrganizationProfile>({
      legalName: "",
      organizationType: undefined,
      nonprofitDetails: {
        status: "501c3",
        verificationStatus: "unverified",
      },
      yearsInOperation: undefined,
      averageMonthlyCases: undefined,
      serviceFocus: [],
      receivesGovernmentFunding: false,
    });

    // Sync form state when business data loads
    useEffect(() => {
      if (business?.organizationProfile) {
        setData({
          legalName: business.organizationProfile.legalName || "",
          organizationType: business.organizationProfile.organizationType,
          nonprofitDetails: business.organizationProfile.nonprofitDetails || {
            status: "501c3",
            verificationStatus: "unverified",
          },
          yearsInOperation: business.organizationProfile.yearsInOperation,
          averageMonthlyCases: business.organizationProfile.averageMonthlyCases,
          serviceFocus: business.organizationProfile.serviceFocus || [],
          receivesGovernmentFunding: business.organizationProfile.receivesGovernmentFunding || false,
        });
      }
    }, [business]);

    useImperativeHandle(ref, () => ({
      submitForm,
    }));

    const submitForm = async () => {
      const id = business?.id ?? account?.id;
      if (!id) {
        console.error("No business or account ID available");
        return;
      }

      try {
        // Build the update payload
        const updatePayload: { organizationProfile: OrganizationProfile } = {
          organizationProfile: {
            ...data,
            // Only include nonprofitDetails if organizationType is nonprofit
            nonprofitDetails:
              data.organizationType === "nonprofit" ? data.nonprofitDetails : undefined,
          },
        };

        await updateBusiness(id, updatePayload);
        onSuccessfulSubmit?.();
      } catch (e) {
        console.error("Error saving organization profile:", e);
      }
    };

    const toggleServiceFocus = (service: ServiceFocus) => {
      const currentServices = data.serviceFocus || [];
      if (currentServices.includes(service)) {
        setData({
          ...data,
          serviceFocus: currentServices.filter((s: ServiceFocus) => s !== service),
        });
      } else {
        setData({
          ...data,
          serviceFocus: [...currentServices, service],
        });
      }
    };

    const isNonprofit = data.organizationType === "nonprofit";

    return (
      <form onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <FieldGroup className="space-y-6">
          {/* Legal Name */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Legal Organization Name</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                The official name of your organization
              </Text>
            </div>
            <div>
              <Field>
                <Input
                  placeholder="e.g., ABC Family Services, Inc."
                  value={data.legalName || ""}
                  onChange={(e) => setData({ ...data, legalName: e.target.value })}
                />
              </Field>
            </div>
          </section>

          {/* Organization Type */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Organization Type</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                Select your organization structure
              </Text>
            </div>
            <div>
              <Field>
                <Select
                  value={data.organizationType || ""}
                  onChange={(e) =>
                    setData({
                      ...data,
                      organizationType: (e.target.value as OrganizationType) || undefined,
                    })
                  }
                >
                  <option value="">Select organization type...</option>
                  {Object.entries(ORGANIZATION_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </section>

          {/* Nonprofit Details - Only shown when nonprofit is selected */}
          {isNonprofit && (
            <div className="p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700 space-y-6">
              <Subheading>Nonprofit Details</Subheading>

              <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>IRS Designation</Label>
                </div>
                <div>
                  <Field>
                    <Select
                      value={data.nonprofitDetails?.status || "501c3"}
                      onChange={(e) =>
                        setData({
                          ...data,
                          nonprofitDetails: {
                            ...data.nonprofitDetails,
                            status: e.target.value as NonprofitStatus,
                            verificationStatus:
                              data.nonprofitDetails?.verificationStatus || "unverified",
                          },
                        })
                      }
                    >
                      {Object.entries(NONPROFIT_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </section>

              <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>EIN (Employer Identification Number)</Label>
                  <Description>Format: XX-XXXXXXX</Description>
                </div>
                <div>
                  <Field>
                    <Input
                      placeholder="12-3456789"
                      value={data.nonprofitDetails?.ein || ""}
                      onChange={(e) =>
                        setData({
                          ...data,
                          nonprofitDetails: {
                            ...data.nonprofitDetails,
                            status: data.nonprofitDetails?.status || "501c3",
                            verificationStatus:
                              data.nonprofitDetails?.verificationStatus || "unverified",
                            ein: e.target.value,
                          },
                        })
                      }
                    />
                  </Field>
                </div>
              </section>

              <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>State of Registration</Label>
                </div>
                <div>
                  <Field>
                    <Input
                      placeholder="e.g., California"
                      value={data.nonprofitDetails?.stateOfRegistration || ""}
                      onChange={(e) =>
                        setData({
                          ...data,
                          nonprofitDetails: {
                            ...data.nonprofitDetails,
                            status: data.nonprofitDetails?.status || "501c3",
                            verificationStatus:
                              data.nonprofitDetails?.verificationStatus || "unverified",
                            stateOfRegistration: e.target.value,
                          },
                        })
                      }
                    />
                  </Field>
                </div>
              </section>
            </div>
          )}

          {/* Years in Operation */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Years in Operation</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                How long has your organization been operating?
              </Text>
            </div>
            <div>
              <Field>
                <Select
                  value={data.yearsInOperation || ""}
                  onChange={(e) =>
                    setData({
                      ...data,
                      yearsInOperation: (e.target.value as YearsInOperationRange) || undefined,
                    })
                  }
                >
                  <option value="">Select...</option>
                  {Object.entries(YEARS_IN_OPERATION_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </section>

          {/* Average Monthly Cases */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Average Monthly Cases</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                How many cases do you typically handle per month?
              </Text>
            </div>
            <div>
              <Field>
                <Select
                  value={data.averageMonthlyCases || ""}
                  onChange={(e) =>
                    setData({
                      ...data,
                      averageMonthlyCases: (e.target.value as MonthlyCasesRange) || undefined,
                    })
                  }
                >
                  <option value="">Select...</option>
                  {Object.entries(MONTHLY_CASES_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </section>

          {/* Services Provided */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Services Provided</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                Select all services your organization provides
              </Text>
            </div>
            <div>
              <div className="grid grid-cols-1 gap-2">
                {SERVICE_FOCUS_OPTIONS.map(({ value, label }) => {
                  const isSelected = (data.serviceFocus || []).includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleServiceFocus(value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${
                        isSelected
                          ? "bg-blue-100 text-blue-700 border-2 border-blue-500 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600"
                          : "bg-zinc-100 text-zinc-700 border-2 border-transparent hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                      }`}
                    >
                      {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
                      <span className={isSelected ? "" : "ml-6"}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Government Funding */}
          <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
            <div className="space-y-1">
              <Subheading>Government Funding</Subheading>
              <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                Does your organization receive government funding?
              </Text>
            </div>
            <div className="flex items-center">
              <Field>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="governmentFunding"
                    checked={data.receivesGovernmentFunding || false}
                    onChange={(e) =>
                      setData({ ...data, receivesGovernmentFunding: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 dark:border-zinc-600 dark:bg-zinc-800"
                  />
                  <Label htmlFor="governmentFunding" className="!mb-0 cursor-pointer">
                    Yes, we receive government funding or contracts
                  </Label>
                </div>
              </Field>
            </div>
          </section>
        </FieldGroup>
      </form>
    );
  }
);

OrganizationProfileForm.displayName = "OrganizationProfileForm";

export default OrganizationProfileForm;
